import { OpenCodeAdapter } from "../runtime/opencode-adapter.js";
import fs from "node:fs/promises";
import path from "node:path";
import { execSync } from "node:child_process";
import { EvidenceLedger } from "../evidence/evidence-ledger.js";

export function createRuntimeRemediationExecutor(cwd: string, ledger: EvidenceLedger | null = null) {
  const adapter = new OpenCodeAdapter({ cwd });

  function getChangedFiles(): string[] {
    try {
      const output = execSync("git status --short", { cwd, encoding: "utf8" });
      return output
        .split("\n")
        .filter(Boolean)
        .map((line) => line.slice(3).trim())
        .filter(Boolean);
    } catch {
      return [];
    }
  }

  return async function runtimeRemediationExecutor({
    attempt,
    maxAttempts,
    mode,
    affectedChecks,
    requestPath,
    result
  }: {
    attempt: number;
    maxAttempts: number;
    mode: string;
    affectedChecks: string[];
    requestPath: string;
    result: any;
  }): Promise<{ applied: boolean; changedFiles?: string[]; evidenceAdded?: string[]; reason?: string }> {
    console.log(`\n[HEALER] Running Runtime/Phoenix Structural Remediation [Attempt ${attempt}/${maxAttempts}]`);
    console.log(`[HEALER] Affected checks: ${affectedChecks.join(", ")}`);

    if (ledger) {
      ledger.logEvent({
        actor: "Phoenix",
        actorType: "runtime-agent",
        observed: true,
        runtime: "opencode",
        eventType: "remediation_start",
        provenance: "opencode-adapter",
        data: {
          attempt,
          affectedChecks,
          event: "remediation.started"
        }
      });
    }

    let requestContent: any = {};
    try {
      requestContent = JSON.parse(await fs.readFile(requestPath, "utf8"));
    } catch {
      // ignore/fallback
    }

    const filesBefore = getChangedFiles();
    const fileContentsBefore: Record<string, string | null> = {};
    for (const file of filesBefore) {
      try {
        fileContentsBefore[file] = await fs.readFile(path.join(cwd, file), "utf8");
      } catch {
        fileContentsBefore[file] = null;
      }
    }
    
    // Extract concrete command outputs for failing checks
    const failedCommands = (result?.evidence?.commands || [])
      .filter((cmd: any) => ["FAIL", "BLOCKED"].includes(cmd.status));
    
    let concreteOutput = "";
    if (failedCommands.length > 0) {
      concreteOutput = failedCommands.map((cmd: any) => {
        return `Command: ${cmd.name} (${Array.isArray(cmd.command) ? cmd.command.join(" ") : cmd.command})
Exit Code: ${cmd.exitCode}
Output:
${cmd.output}`;
      }).join("\n\n");
    } else {
      concreteOutput = JSON.stringify(result?.quality || result || {}, null, 2);
    }

    const promptMsg = `We are in remediation mode (attempt ${attempt}/${maxAttempts}).
The following validation checks have failed:
${affectedChecks.join(", ")}

Details of the failure:
\`\`\`
${concreteOutput}
\`\`\`

Please inspect the code, identify the root cause of these failures (e.g. failing tests, syntax issues, build errors), and write a correction to the relevant files in the workspace to make the tests/validations pass.`;

    console.log(`[HEALER] Sending structural healing request to Phoenix agent...`);
    const runResult = await adapter.execute(promptMsg, {
      agent: "Phoenix"
    });

    const filesAfter = getChangedFiles();
    const changedFiles: string[] = [];
    for (const file of filesAfter) {
      if (!filesBefore.includes(file)) {
        changedFiles.push(file);
      } else {
        try {
          const contentAfter = await fs.readFile(path.join(cwd, file), "utf8");
          if (contentAfter !== fileContentsBefore[file]) {
            changedFiles.push(file);
          }
        } catch {
          if (fileContentsBefore[file] !== null) {
            changedFiles.push(file);
          }
        }
      }
    }
    const success = runResult.success && changedFiles.length > 0;

    if (!runResult.success) {
      console.log(`[HEALER] Phoenix remediation failed: ${runResult.error || "Unknown error"}`);
      return {
        applied: false,
        reason: `Phoenix runtime execution failed: ${runResult.error || "Unknown error"}`
      };
    }

    if (changedFiles.length === 0) {
      console.log(`[HEALER] Phoenix execution succeeded but no files were modified.`);
      return {
        applied: false,
        reason: "Phoenix runtime executed but did not modify any files in the workspace."
      };
    }

    console.log(`[HEALER] Phoenix successfully executed remediation commands.`);
    console.log(`[HEALER] Files modified: ${changedFiles.join(", ")}`);

    return {
      applied: true,
      changedFiles,
      evidenceAdded: ["phoenix-healing-run"]
    };
  };
}
